import {
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
} from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import { skyReticleProjection } from './135-sky-reticle.mjs';

/** Read only the live, exposed contact proxies and fully open physical portals.
 * Coordinates/radii are metres. Closed, destroyed and future objectives never
 * acquire an inviting outline; this presentation cannot change collision. */
export function skyObjectiveZones(state) {
  const live = state.leviathan;
  if (state.status !== 'playing' || !live || state.boss?.defeated) return [];
  const encounter = live.encounter;
  const phase = encounter.phase;
  const zones = live.components
    .filter((part) => part.vulnerable && part.hp > 0)
    .slice(0, 7)
    .map((part) => ({
      kind: 'target',
      id: part.componentId,
      center: { x: part.x, y: part.y, z: part.z },
      radius: part.radius,
    }));
  const key =
    phase === 'entry' && live.systems?.jaw === 1
      ? 'mouth'
      : phase === 'breakout' && live.systems?.breach === 1
        ? 'breach'
        : null;
  const portal = key && encounter.geometry[key];
  if (portal)
    zones.push({
      kind: 'portal',
      id: key,
      center: skyFlightPointToWorld(encounter.frame, portal.center),
      normal: skyFlightVectorToWorld(encounter.frame, portal.normal),
      up: skyFlightVectorToWorld(encounter.frame, portal.up),
      halfWidth: portal.halfWidth,
      halfHeight: portal.halfHeight,
    });
  return zones;
}

/** Eight depth-tested outline slots share two instanced draws (352 triangles).
 * Open circles identify shooting targets; four inward corners identify a
 * fly-through opening. No text, filled screen tint, light, texture or bloom.
 * Resource ownership/disposal remains with the renderer. */
export function createSkyObjectiveZones(T, scene, own) {
  const ring = own(new T.RingGeometry(0.91, 1, 24));
  const vertices = [];
  const rectangle = (x0, y0, x1, y1) =>
    vertices.push(x0, y0, 0, x1, y0, 0, x1, y1, 0, x0, y0, 0, x1, y1, 0, x0, y1, 0);
  for (const x of [-1, 1])
    for (const y of [-1, 1]) {
      rectangle(x < 0 ? -1 : 0.68, y < 0 ? -1 : 0.94, x < 0 ? -0.68 : 1, y < 0 ? -0.94 : 1);
      rectangle(x < 0 ? -1 : 0.94, y < 0 ? -1 : 0.68, x < 0 ? -0.94 : 1, y < 0 ? -0.68 : 1);
    }
  const corners = own(
    new T.BufferGeometry().setAttribute('position', new T.Float32BufferAttribute(vertices, 3)),
  );
  const paint = own(
    new T.MeshBasicMaterial({
      color: '#8affcf',
      side: T.DoubleSide,
      depthWrite: false,
      transparent: true,
      opacity: 0.98,
      toneMapped: false,
    }),
  );
  const targets = own(new T.InstancedMesh(ring, paint, 7)),
    portals = own(new T.InstancedMesh(corners, paint, 1));
  targets.name = 'sky-objective-target-zones';
  portals.name = 'sky-objective-flight-zone';
  for (const batch of [targets, portals]) {
    batch.count = 0;
    batch.frustumCulled = false;
    // Paint after translucent lens artwork while retaining opaque hull depth.
    batch.renderOrder = 30;
    scene.add(batch);
  }
  const transform = new T.Object3D(),
    direction = new T.Vector3(),
    up = new T.Vector3(),
    right = new T.Vector3(),
    basis = new T.Matrix4();
  let candidates = 0;
  return {
    diagnostics: () => ({ targets: targets.count, portals: portals.count, candidates }),
    draw(state, camera, active = true, width = 390, height = 844) {
      targets.count = portals.count = 0;
      candidates = 0;
      if (!active) return;
      const zones = skyObjectiveZones(state);
      candidates = zones.length;
      for (const zone of zones) {
        if (!zone.center || !['x', 'y', 'z'].every((key) => Number.isFinite(zone.center[key])))
          continue;
        transform.position.set(zone.center.x, zone.center.y, zone.center.z);
        if (zone.kind === 'target') {
          const projected = skyReticleProjection(camera, zone.center, width, height);
          if (!projected.visible) continue;
          direction.copy(camera.position).sub(transform.position).normalize();
          transform.position.addScaledVector(direction, zone.radius);
          transform.quaternion.copy(camera.quaternion);
          // A callout surrounds the visible pod instead of disappearing inside
          // its opaque skin. It marks a centre, never enlarges the hit sphere.
          transform.scale.setScalar(Math.max(zone.radius * 1.8, projected.scale * 0.43));
          transform.updateMatrix();
          targets.setMatrixAt(targets.count++, transform.matrix);
        } else {
          direction.set(zone.normal.x, zone.normal.y, zone.normal.z);
          up.set(zone.up.x, zone.up.y, zone.up.z);
          right.crossVectors(up, direction).normalize();
          basis.makeBasis(right, up, direction);
          transform.quaternion.setFromRotationMatrix(basis);
          transform.position.addScaledVector(direction, 0.04);
          transform.scale.set(zone.halfWidth * 0.87, zone.halfHeight * 0.87, 1);
          transform.updateMatrix();
          portals.setMatrixAt(portals.count++, transform.matrix);
        }
      }
      targets.instanceMatrix.needsUpdate = portals.instanceMatrix.needsUpdate = true;
    },
  };
}
